import { useCallback, useMemo, useRef, useState } from 'react';
import type { FieldErrors, UseFormSetError } from 'react-hook-form';

import { LeaseDetailsForm } from '@/features/LeaseDetails';
import type { LeaseDetailsFormConfig, SubmitContext } from '@/features/LeaseDetails';
import { checkIfPmi } from '@/utils/commonUtils';
import { MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE } from '@/utils/constants';
import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';
import type { Renter } from '@/utils/types/renter.interface';

import { inviteLeaseDetailsSchema } from './invite-lease-details.schema';
import { useInviteFormTracking } from './hooks';
import type { UseInviteLeaseDetailsControllerResult } from './hooks';
import { InviteLeaseDetailsSharedBody } from './invite-lease-details.shared-body';
import { InviteLeaseDetailsSharedFooter } from './invite-lease-details.shared-footer';
import type { InviteFlowMilestoneKey } from './types';

export type InviteLeaseDetailsSharedFormProps = {
  /** Hoisted by the drawer so legacy and shared share one controller instance. */
  controller: UseInviteLeaseDetailsControllerResult;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
};

const EMPTY_SECTION = { visible: false as const, fields: {} };

// Invite drawer rendered on the shared <LeaseDetailsForm> engine (OTTERS-4671,
// Path A "engine as form-host"). The engine owns the RHF instance, the injected
// `inviteLeaseDetailsSchema`, the submit lifecycle, and the footer; the actual
// fields live in InviteLeaseDetailsSharedBody (reused legacy section components)
// wired through `ui.slots.top`. The four engine sections stay hidden — invite's
// config-native conversion is P4.3.
export const InviteLeaseDetailsSharedForm = ({
  controller,
  onMilestoneReached,
}: InviteLeaseDetailsSharedFormProps) => {
  const { user, errorMsg, isSmbInvite, useNewForm, customCoverageDisabled, onSubmit } = controller;

  // address.source-derived; lifted from the body so the SDR cap stays reactive.
  const [isSelfEnrolled, setIsSelfEnrolled] = useState(false);
  // Written by the body, read by `buildPayload` (mirrors the legacy submit).
  const selectedSmbPricingRef = useRef<unknown>(undefined);

  const selfEnrolledSDRLimit = useMemo(() => {
    if (!user) return undefined;
    if (checkIfPmi(user)) return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.PMI;
    if (isSelfEnrolled) return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.SELF_ENROLLED;
    return undefined;
  }, [user, isSelfEnrolled]);

  const { trackFlowStarted, trackValidationErrors } = useInviteFormTracking({
    onFlowStarted: () => onMilestoneReached?.('hadFlowStarted'),
  });

  const schema = useMemo(
    () =>
      inviteLeaseDetailsSchema(selfEnrolledSDRLimit, customCoverageDisabled, useNewForm === true),
    [selfEnrolledSDRLimit, customCoverageDisabled, useNewForm],
  );

  const handleValidationError = useCallback(
    (errors: FieldErrors) => {
      onMilestoneReached?.('hadSubmitAttempt');
      trackValidationErrors(errors as Record<string, unknown>, 'client');
    },
    [onMilestoneReached, trackValidationErrors],
  );

  const config = useMemo<LeaseDetailsFormConfig>(
    () => ({
      sections: {
        leaseInformation: EMPTY_SECTION,
        applicants: { ...EMPTY_SECTION, min: 0, max: 6, primaryLocked: false },
        screening: { ...EMPTY_SECTION, variant: isSmbInvite ? 'smb' : 'institutional' },
        coverage: EMPTY_SECTION,
      },
      submit: {
        buildPayload: (values) => {
          // The engine reserves/seeds an `applicants` key; drop it so it can't
          // leak into the invite wire payload (parity bar = empty diff).
          const { applicants: _applicants, ...rest } = values;
          return useNewForm ? { ...rest, selectedSmbPricing: selectedSmbPricingRef.current } : rest;
        },
        submit: (payload, ctx: SubmitContext) => {
          onMilestoneReached?.('hadSubmitAttempt');
          return onSubmit(
            payload as Renter,
            ctx.setError as UseFormSetError<Renter>,
            ctx.attemptNumber,
          );
        },
      },
      ui: {
        hideDefaultActions: true,
        contentSpacing: 0,
        slots: {
          top: (
            <InviteLeaseDetailsSharedBody
              user={user}
              isSmbInvite={isSmbInvite}
              newCoveragesEnabled={useNewForm}
              customCoverageDisabled={customCoverageDisabled}
              selfEnrolledSDRLimit={selfEnrolledSDRLimit}
              errorMsg={errorMsg}
              onMilestoneReached={onMilestoneReached}
              onSelfEnrolledChange={setIsSelfEnrolled}
              selectedSmbPricingRef={selectedSmbPricingRef}
            />
          ),
          bottom: <InviteLeaseDetailsSharedFooter />,
        },
      },
    }),
    [
      user,
      isSmbInvite,
      useNewForm,
      customCoverageDisabled,
      selfEnrolledSDRLimit,
      errorMsg,
      onMilestoneReached,
      onSubmit,
    ],
  );

  const initialValues = useMemo(
    () => ({
      apartmentNumber: '',
      approvalStatus: '',
      customCoverage: false,
      applicantEmails: [{ value: '' }],
      securityDepositCoverage: SecurityDepositTypes.CompanyDefault,
      rentCoverage: RentCoverageTypes.CompanyDefault,
    }),
    [],
  );

  return (
    <div
      onFocusCapture={() => trackFlowStarted('field_focus')}
      onClickCapture={() => trackFlowStarted('click')}
      onKeyDownCapture={() => trackFlowStarted('typing')}
    >
      <LeaseDetailsForm
        config={config}
        initialValues={initialValues}
        schema={schema}
        mode="all"
        shouldUnregister
        onValidationError={handleValidationError}
      />
    </div>
  );
};
